/**
 * DiscoveryForm Component
 * MP-1.1: Replace fake conversation with honest 18-question form
 *
 * Features:
 * - 4 sections with 18 total questions
 * - Progress tracking and section navigation
 * - Auto-save to localStorage every 30s
 * - Client-side validation
 * - Fires discoveryComplete event on submission
 */

import { Modal } from './Modal.js';
import { DiscoveryRepository } from '../repositories/DiscoveryRepository.js';
import { NexusConfig } from '../config.js';
// MP-07: the five choices come from the teaser's ONE question source — same question, same values,
// both surfaces, by import rather than by copy (esbuild bundles the CommonJS module).
import { PILLS, TONES, INTENSITY_STOPS, keepPromotionType, firstIncompleteSection } from '../../../../section-builder/backend/services/teaserQuestions.js';

// MP-07 (review): EVERY value interpolated into markup goes through here — a typed double quote
// must never become a live attribute, and a typed closing tag must never break out of a textarea.
// Same contract as the teaser renderer's esc().
const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export class DiscoveryForm {
        constructor(campaignId = null, existingData = null, options = {}) {
            this.campaignId = campaignId;
            // editMode = true when editing existing campaign (shows Back to Dashboard button)
            this.editMode = options.editMode || false;
            this.viewOnly = options.viewOnly || false;
            this.locked = options.locked || false; // cascade lock — hides Edit button
            // externalMode = true when rendered on the external prospect-facing form page
            this.externalMode = options.externalMode || false;
            // Scope localStorage draft key per shortId in external mode to prevent collisions
            this.draftKey = this.externalMode && options.shortId
                ? `discoveryFormDraft_${options.shortId}`
                : 'discoveryFormDraft';
            const loadDraftAutomatically = options.loadDraft || (typeof options === 'boolean' && options);

            // NEXUS mode detection
            this.isNexusMode = NexusConfig.isNexusMode();
            if (this.isNexusMode) {
                console.log('🔗 DiscoveryForm: NEXUS mode - contentType selector enabled');
            }

            // Priority: existingData > draft (if loadDraftAutomatically) > empty form
            if (existingData) {
                this.formData = existingData;
                // A retired campaign type is dropped on load — founder ruling, 10 August 2026. The
                // rest of the discovery loads as normal; only the type is emptied, so the
                // completeness gate names it and the person picks one of the five. Never swapped.
                this.formData.promotionType = keepPromotionType(this.formData.promotionType);
                console.log('[DiscoveryForm constructor] existingData.contentType:', JSON.stringify(existingData.contentType), '| type:', typeof existingData.contentType);
                // Ensure all required fields exist (defensive handling for old schema)
                this.ensureFormDataStructure();
                console.log('[DiscoveryForm constructor] AFTER ensureFormDataStructure - contentType:', JSON.stringify(this.formData.contentType));
            } else if (loadDraftAutomatically) {
                this.formData = this.loadDraft() || this.getEmptyForm();
                this.formData.promotionType = keepPromotionType(this.formData.promotionType);
            } else {
                this.formData = this.getEmptyForm();
            }

            // CRITICAL: Include campaignId in formData for subsequent saves
            // This prevents duplicate campaign creation in edit mode
            if (this.campaignId && !this.formData.campaignId) {
                this.formData.campaignId = this.campaignId;
            }

            // MP-04: Pre-fill empty form fields from NEXUS org profile
            this.prefilledFields = new Set();
            this._prefillFromOrgProfile();

            // MP-07: the form OPENS on the promoting choice — its own tab ahead of the rest
            // (founder decision 16). Section numbers 1-5 are unchanged; the choice is section 0.
            this.currentSection = 0;
            // Positioning exists in BOTH modes (the send requires a voice and a push register).
            // Brand Voice remains owner-mode only, as it always was.
            this.sections = this.externalMode ? 5 : 6;
            this.autosaveInterval = null;
        }

        /**
         * MP-04: Pre-fill empty discovery form fields from NEXUS org profile.
         * Only fills fields that are currently empty. Tracks which fields were pre-filled
         * so we can show indicators in the UI.
         */
        _prefillFromOrgProfile() {
            try {
                const profileJson = sessionStorage.getItem('nexus_org_profile');
                if (!profileJson) return;

                const orgProfile = JSON.parse(profileJson);

                // websiteUrl <- org profile websiteUrl
                if (orgProfile.websiteUrl && !this.formData.websiteUrl) {
                    this.formData.websiteUrl = orgProfile.websiteUrl;
                    this.prefilledFields.add('websiteUrl');
                }

                // productCategory <- org profile industry
                if (orgProfile.industry && !this.formData.productCategory) {
                    this.formData.productCategory = orgProfile.industry;
                    this.prefilledFields.add('productCategory');
                }

                // targetBuyer <- org profile targetAudience
                if (orgProfile.targetAudience && !this.formData.targetBuyer) {
                    this.formData.targetBuyer = orgProfile.targetAudience;
                    this.prefilledFields.add('targetBuyer');
                }

                if (this.prefilledFields.size > 0) {
                    console.log('[DiscoveryForm] Pre-filled from org profile:', Array.from(this.prefilledFields));
                }
            } catch (e) {
                console.warn('[DiscoveryForm] Failed to load org profile for pre-fill:', e.message);
            }
        }

        /**
         * Ensure formData has all required fields with proper defaults
         * Handles backward compatibility with old discovery schema
         * ONLY fills in missing fields, preserves existing non-empty values
         */
        ensureFormDataStructure() {
            const defaults = this.getEmptyForm();

            // Only add fields that are truly missing (undefined/null)
            // Do NOT overwrite existing values (even empty strings from mapping)
            for (const key in defaults) {
                if (this.formData[key] === undefined || this.formData[key] === null) {
                    this.formData[key] = defaults[key];
                }
            }

            // MP-07: the registry stores the deal answer as a boolean; the form speaks yes/no.
            // A record written by the teaser opens with the radio already picked, not blank.
            if (this.formData.offerHasDeal === true) this.formData.offerHasDeal = 'yes';
            else if (this.formData.offerHasDeal === false) this.formData.offerHasDeal = 'no';

            // Ensure critical arrays are arrays (not strings or other types)
            if (!Array.isArray(this.formData.painPoints)) {
                this.formData.painPoints = ['', '', ''];
            }
            if (!Array.isArray(this.formData.keyBenefits)) {
                this.formData.keyBenefits = ['', '', ''];
            }
            if (!Array.isArray(this.formData.proofAssets)) {
                this.formData.proofAssets = [];
            }

            // Pad arrays to minimum length if they exist but are too short
            while (this.formData.painPoints.length < 3) {
                this.formData.painPoints.push('');
            }
            while (this.formData.keyBenefits.length < 3) {
                this.formData.keyBenefits.push('');
            }
        }

        /**
         * Check if draft exists and prompt user to resume or start fresh
         * @returns {Promise<Object|null>} Draft data if user wants to resume, null otherwise
         */
        static async checkAndPromptForDraft() {
            const draft = localStorage.getItem('discoveryFormDraft');
            if (!draft) return null;

            const shouldResume = await Modal.confirmModal(
                '📝 Found Previous Session',
                'Do you want to resume where you left off?\n\nClick "Confirm" to resume previous session\nClick "Cancel" to start fresh'
            );

            if (shouldResume) {
                return JSON.parse(draft);
            } else {
                // User chose to start fresh - clear the draft
                localStorage.removeItem('discoveryFormDraft');
                return null;
            }
        }

        getEmptyForm() {
            return {
                // Section 1: Product Info
                promotionType: '',
                // MP-07: the thing-specific facts (event/news paths) and the every-path additions
                eventName: '', eventDate: '', eventStartTime: '', eventEndTime: '',
                eventFormat: '', eventLocation: '', eventHost: '', eventHostCredential: '',
                announcementSubject: '', announcementWhen: '',
                offerHasDeal: '', offerNormalPrice: '', offerPrice: '', offerEnds: '',
                problemCost: '', problemCostPeriod: '', proofNumber: '', adIntensity: 3, productDescription: '', brandTones: '', proofQuoteName: '', proofQuoteRole: '', actionUrl: '',
                // ads-brief-per-type-cutover step 5: each type's own answers, plus the one optional
                // question offered on every path.
                announcementImpact: '', courseStarts: '', courseTeacher: '',
                serviceArea: '', serviceAvailability: '', topObjection: '',
                productName: '',
                websiteUrl: '',
                linkedinUrl: '',
                productCategory: '',
                pricePoint: '',
                purchaseType: '',

                // Section 2: Audience
                targetBuyer: '',
                awarenessLevel: '',
                buyerPsychology: '',
                purchaseTimeline: '',
                triggerEvents: '',
                failedApproaches: '',

                // Section 3: Content
                painPoints: ['', '', ''],
                keyBenefits: ['', '', ''],
                uniqueValue: '',
                enemyName: '',
                prospectLanguage: '',
                proofAssets: [],
                socialProof: '',

                // Section 4: Offer
                contentType: '',  // NEXUS mode: 'cold' or 'warm'
                campaignGoal: '',  // MP-03: campaign goal — feeds framework scoring + voice
                offerDetails: '',
                callToAction: '',
                trafficSource: '',
                conversionDestination: '',
                // Section 5: Brand voice (MP-02) — owner mode only
                brandVoice: ''
            };
        }

        render() {
            const container = document.getElementById('discovery-form-container');

            // Determine header and button text
            const headerText = this.externalMode ? 'Campaign Discovery Questionnaire'
                : this.viewOnly ? 'View Campaign Discovery' : 'Campaign Discovery';
            const submitText = this.externalMode ? 'Submit Responses'
                : this.viewOnly ? 'Back to Dashboard' : 'Continue to Framework Selection';

            container.innerHTML = `
                ${this.viewOnly ? `
                <div style="background: var(--bg-tertiary); border: 1px solid var(--border-primary); border-radius: 0.5rem; padding: 1rem; margin-bottom: 1rem; text-align: center;">
                    <p style="color: var(--text-secondary); margin: 0;">${this.locked
                        ? '🔒 <strong>Locked</strong> - Editing is disabled because downstream content has been published'
                        : '👁️ <strong>View Mode</strong> - This is a read-only view of the discovery data'}</p>
                </div>` : ''}

                <div class="form-header">
                    <div class="form-header-top">
                        <h1>${headerText}</h1>
                        <div class="discovery-io-buttons">
                            <button type="button" id="download-discovery-btn" class="btn-icon-labeled" title="Download Discovery Data">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 10.5l-3.5-3.5h2.5v-5h2v5h2.5l-3.5 3.5zm-6 2h12v1.5h-12v-1.5z"/></svg>
                                <span>Download</span>
                            </button>
                            ${this.externalMode || this.viewOnly ? '' : `<button type="button" id="upload-discovery-btn" class="btn-icon-labeled" title="Upload Discovery Data">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 2l3.5 3.5h-2.5v5h-2v-5h-2.5l3.5-3.5zm-6 9.5h12v1.5h-12v-1.5z"/></svg>
                                <span>Upload</span>
                            </button>
                            <input type="file" id="discovery-file-input" accept=".json" style="display: none;" />`}
                            ${!this.externalMode && this.campaignId ? `<button type="button" id="share-form-btn" class="btn-icon-labeled" title="Share Discovery Form">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M11 2.5a2.5 2.5 0 0 1 0 5 2.5 2.5 0 0 1-2.086-1.12L5.929 8.066a2.5 2.5 0 0 1 0 1.868l2.985 1.686A2.5 2.5 0 1 1 8.07 12.88l-2.985-1.686a2.5 2.5 0 1 1 0-4.388l2.985-1.686A2.5 2.5 0 0 1 11 2.5z"/></svg>
                                <span>Share</span>
                            </button>
                            <button type="button" id="share-teaser-btn" class="btn-icon-labeled" title="Ad Builder (8 quick questions build this campaign's discovery)">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1.5l1.8 3.9 4.2.5-3.1 2.9.9 4.2L8 11.3 4.3 13l.9-4.2L2.1 5.9l4.2-.5L8 1.5z"/></svg>
                                <span>Ad Builder</span>
                            </button>` : ''}
                        </div>
                    </div>
                    <div class="progress-bar">
                        <div class="progress-fill" style="width: ${this.getProgressPercent()}%"></div>
                    </div>
                    <p class="progress-text">${this.getCompletedQuestions()}/${this.getTotalQuestions()} questions complete</p>
                </div>

                <div class="section-tabs">
                    ${this.renderSectionTabs()}
                </div>

                <form id="discovery-form" class="discovery-form">
                    ${this.renderCurrentSection()}
                </form>

                <div class="form-actions">
                    ${this.currentSection > 0 ? '<button type="button" id="prev-btn" class="btn-secondary">Previous</button>' : ''}
                    ${this.viewOnly && !this.locked ? '<button type="button" id="edit-btn" class="btn-secondary">Edit Discovery</button>' : ''}
                    ${this.editMode || this.viewOnly ? '<button type="button" id="cancel-btn" class="btn-secondary">Back to Dashboard</button>' : ''}
                    ${this.currentSection < this.sections ? '<button type="button" id="next-btn" class="btn-primary">Next Section</button>' : ''}
                    ${this.currentSection === this.sections && !this.viewOnly ? `<button type="button" id="submit-btn" class="btn-primary">${submitText}</button>` : ''}
                </div>
                <p class="field-error" id="submit-hint" style="display:none"></p>

                <div class="autosave-indicator" id="autosave-indicator" style="display: none;">
                    ${this.editMode ? 'Changes saved' : 'Draft saved'}
                </div>
            `;

            // Disable all inputs if viewOnly mode (except file input for upload)
            if (this.viewOnly) {
                setTimeout(() => {
                    const allInputs = container.querySelectorAll('input, textarea, select');
                    allInputs.forEach(input => {
                        if (input.type === 'file') return;
                        input.disabled = true;
                        input.style.opacity = '0.7';
                        input.style.cursor = 'not-allowed';
                    });
                }, 0);
            }

            this.attachEventListeners();
            // Viewing arms nothing: a read-only screen that saves itself every thirty seconds is
            // how simply looking at a campaign erased answers it had never even shown.
            if (!this.viewOnly) this.startAutosave();

            // MP-04: Show pre-fill indicators on fields populated from org profile
            this._showPrefillIndicators();

            // A refused save must not vanish because the person moved to another tab.
            if (this.lastSaveFailure) this.showAutosaveIndicator(this.lastSaveFailure);
        }

        /**
         * MP-04: Add subtle "from org profile" indicator next to pre-filled fields.
         * Indicators are removed when the user edits the field.
         */
        _showPrefillIndicators() {
            if (this.prefilledFields.size === 0) return;

            for (const fieldId of this.prefilledFields) {
                const input = document.getElementById(fieldId);
                if (!input) continue;

                // Create indicator element
                const indicator = document.createElement('span');
                indicator.className = 'org-profile-indicator';
                indicator.dataset.fieldId = fieldId;
                indicator.textContent = 'from org profile';
                indicator.style.cssText = 'font-size: 0.75rem; color: var(--text-tertiary, #999); font-style: italic; margin-left: 0.5rem;';

                // Insert after the input's label
                const label = input.closest('.form-group')?.querySelector('label');
                if (label) {
                    label.appendChild(indicator);
                }

                // Remove indicator when user edits the field
                input.addEventListener('input', () => {
                    this.prefilledFields.delete(fieldId);
                    const ind = input.closest('.form-group')?.querySelector('.org-profile-indicator');
                    if (ind) ind.remove();
                }, { once: true });
            }
        }

        renderSectionTabs() {
            const sections = [
                {num: 0, name: 'Promoting'},
                {num: 1, name: 'Product Info'},
                {num: 2, name: 'Audience'},
                {num: 3, name: 'Content'},
                {num: 4, name: 'Offer'}
            ];
            sections.push({num: 5, name: 'Tone'});
            // Brand Voice keeps its OWN tab, after Positioning (founder ruling 2026-08-05).
            if (!this.externalMode) sections.push({num: 6, name: 'Brand voice'});

            return sections.map(section => `
                <div class="section-tab ${this.currentSection === section.num ? 'active' : ''}"
                     data-section="${section.num}">
                    <span class="section-number">${section.num + 1}</span>
                    <span class="section-name">${section.name}</span>
                    <span class="section-progress">${this.getSectionProgress(section.num)}</span>
                </div>
            `).join('');
        }

        renderCurrentSection() {
            switch (this.currentSection) {
                case 0: return this.renderSection0();
                case 1: return this.renderSection1();
                case 2: return this.renderSection2();
                case 3: return this.renderSection3();
                case 4: return this.renderSection4();
                case 5: return this.renderSection5();
                case 6: return this.renderSection6();
            }
        }

        /**
         * REMOVED 10 August 2026 — founder ruling, reversing decision 19 of 2 August.
         *
         * A row saved with a retired promoting value used to show that value back as a sixth option
         * labelled "(previously saved)". A person could pick it again, and the ads service — which
         * accepts exactly the five — refused the request. That is the 400 seen in production.
         *
         * The value is now dropped when the discovery loads. Everything else on the form loads as
         * normal; only the type is empty, so the completeness gate names it as the missing answer.
         * It is never swapped for another type: nothing maps "personal-brand" onto one of the five,
         * and choosing for them would write a campaign they never asked for.
         */
        legacyPromotionOption() {
            return '';
        }

        renderSection0() {
            return `
                <div class="form-section">
                    <h2>What are you promoting?</h2>

                    <div class="form-group">
                        <fieldset class="radio-group" data-required="true">
                            <legend>Pick one. The questions that follow fit what you pick. <span class="required-indicator">*</span></legend>
                            ${PILLS.map(p => `<label><input type="radio" name="promotionType" value="${p.key}" ${this.formData.promotionType === p.key ? 'checked' : ''}> <span aria-hidden="true">${p.icon}</span> ${p.name}</label>`).join('\n                            ')}
                            ${this.legacyPromotionOption()}
                        </fieldset>
                        <span class="field-error" id="error-promotionType"></span>
                    </div>
                    ${this.formData.promotionType === 'event' ? `
                    <div class="form-group">
                        <label for="eventName">Event name <span class="required-indicator">*</span></label>
                        <input type="text" id="eventName" value="${esc(this.formData.eventName)}" required>
                        <span class="field-error" id="error-eventName"></span>
                    </div>
                    <div class="form-group">
                        <label for="eventDate">Date <span class="required-indicator">*</span></label>
                        <input type="text" id="eventDate" value="${esc(this.formData.eventDate)}" required placeholder="Tuesday 14 October">
                        <span class="field-error" id="error-eventDate"></span>
                    </div>
                    <div class="form-group">
                        <label for="eventStartTime">Start time <span class="required-indicator">*</span></label>
                        <input type="text" id="eventStartTime" value="${esc(this.formData.eventStartTime)}" required placeholder="7pm">
                        <span class="field-error" id="error-eventStartTime"></span>
                    </div>
                    <div class="form-group">
                        <label for="eventEndTime">End time <span class="optional">(optional)</span></label>
                        <input type="text" id="eventEndTime" value="${esc(this.formData.eventEndTime)}" placeholder="9pm">
                        <span class="field-error" id="error-eventEndTime"></span>
                    </div>
                    <div class="form-group">
                        <fieldset class="radio-group" data-required="true">
                            <legend>In person, online, or both? <span class="required-indicator">*</span></legend>
                            <label><input type="radio" name="eventFormat" value="In person" ${this.formData.eventFormat === 'In person' ? 'checked' : ''}> In person</label>
                            <label><input type="radio" name="eventFormat" value="Online" ${this.formData.eventFormat === 'Online' ? 'checked' : ''}> Online</label>
                            <label><input type="radio" name="eventFormat" value="Both" ${this.formData.eventFormat === 'Both' ? 'checked' : ''}> Both</label>
                        </fieldset>
                        <span class="field-error" id="error-eventFormat"></span>
                    </div>
                    <div class="form-group">
                        <label for="eventLocation">Where <span class="required-indicator">*</span></label>
                        <input type="text" id="eventLocation" value="${esc(this.formData.eventLocation)}" required placeholder="The Mill in Leeds">
                        <span class="field-error" id="error-eventLocation"></span>
                    </div>
                    <div class="form-group">
                        <label for="eventHost">Hosted or led by <span class="required-indicator">*</span></label>
                        <input type="text" id="eventHost" value="${esc(this.formData.eventHost)}" required>
                        <span class="field-error" id="error-eventHost"></span>
                    </div>
                    <div class="form-group">
                        <label for="eventHostCredential">Their one-line credential <span class="optional">(optional)</span></label>
                        <input type="text" id="eventHostCredential" value="${esc(this.formData.eventHostCredential)}" placeholder="Has trained 400 cafe owners">
                        <span class="field-error" id="error-eventHostCredential"></span>
                    </div>` : ''}
                    ${this.formData.promotionType === 'news' ? `
                    <div class="form-group">
                        <label for="announcementSubject">What is changing? <span class="required-indicator">*</span></label>
                        <input type="text" id="announcementSubject" value="${esc(this.formData.announcementSubject)}" required placeholder="We are opening a second branch in Leeds">
                        <span class="field-error" id="error-announcementSubject"></span>
                    </div>
                    <div class="form-group">
                        <label for="announcementWhen">When it happens or goes live <span class="required-indicator">*</span></label>
                        <input type="text" id="announcementWhen" value="${esc(this.formData.announcementWhen)}" required placeholder="3 September">
                        <span class="field-error" id="error-announcementWhen"></span>
                    </div>
                    <div class="form-group">
                        <label for="announcementImpact">What this changes for them <span class="required-indicator">*</span></label>
                        <input type="text" id="announcementImpact" value="${esc(this.formData.announcementImpact)}" required data-min-chars="15" placeholder="You no longer wait five days for a service">
                        <small>The difference between a notice and an ad</small>
                        <span class="field-error" id="error-announcementImpact"></span>
                    </div>` : ''}

                    ${this.formData.promotionType === 'course' ? `
                    <div class="form-group">
                        <label for="courseStarts">When it starts, or that it is self-paced <span class="required-indicator">*</span></label>
                        <input type="text" id="courseStarts" value="${esc(this.formData.courseStarts)}" required placeholder="Starts 14 October, or work at your own pace">
                        <small>Enrolment closes when a cohort begins — that is the course's own urgency</small>
                        <span class="field-error" id="error-courseStarts"></span>
                    </div>
                    <div class="form-group">
                        <label for="courseTeacher">Who teaches it <span class="required-indicator">*</span></label>
                        <input type="text" id="courseTeacher" value="${esc(this.formData.courseTeacher)}" required placeholder="Sarah Ellis, who has trained 400 cafe owners">
                        <span class="field-error" id="error-courseTeacher"></span>
                    </div>` : ''}

                    ${this.formData.promotionType === 'service' ? `
                    <div class="form-group">
                        <label for="serviceArea">The area you cover <span class="required-indicator">*</span></label>
                        <input type="text" id="serviceArea" value="${esc(this.formData.serviceArea)}" required placeholder="Leeds and 20 miles around it">
                        <small>Without it the ads cannot say "your local"</small>
                        <span class="field-error" id="error-serviceArea"></span>
                    </div>
                    <div class="form-group">
                        <label for="serviceAvailability">How soon you can start <span class="required-indicator">*</span></label>
                        <input type="text" id="serviceAvailability" value="${esc(this.formData.serviceAvailability)}" required placeholder="Same day, or booking into March">
                        <small>Often the deciding factor, and urgency with no discount attached</small>
                        <span class="field-error" id="error-serviceAvailability"></span>
                    </div>` : ''}
                </div>
            `;
        }

        renderSection1() {
            return `
                <div class="form-section">
                    <h2>Product Information</h2>

                    <div class="form-group">
                        <label for="productName">${this.formData.promotionType === 'personal-brand' ? 'Your Name' : 'Product/Brand Name'} <span class="required-indicator">*</span></label>
                        <input type="text" id="productName" value="${esc(this.formData.productName)}" required>
                        <span class="field-error" id="error-productName"></span>
                    </div>

                    <div class="form-group">
                        <!-- Required on EVERY type since 11 August 2026. The ads service made it
                             unconditionally required at 22:33 on 10 August, minimum twelve characters,
                             AFTER both surfaces had been aligned that morning — so an event and an
                             announcement were still told it was optional and were then refused. -->
                        <label for="productDescription">In one sentence, what is it? <span class="required-indicator">*</span></label>
                        <textarea id="productDescription" rows="2" placeholder="A cast-iron pan that goes from hob to oven to table.">${esc(this.formData.productDescription)}</textarea>
                        <small>The teaser asks this too; both land in the same place</small>
                    </div>

                    <div class="form-group">
                        <label for="websiteUrl">Website URL <span class="optional">(optional)</span></label>
                        <input type="url" id="websiteUrl" value="${esc(this.formData.websiteUrl)}" placeholder="https://example.com">
                    </div>

                    <div class="form-group">
                        <label for="linkedinUrl">LinkedIn URL <span class="optional">(optional)</span></label>
                        <input type="url" id="linkedinUrl" value="${esc(this.formData.linkedinUrl)}" placeholder="https://linkedin.com/in/username">
                    </div>

                    <div class="form-group">
                        <label for="productCategory">${this.formData.promotionType === 'personal-brand' ? 'Your Positioning' : 'Product Category'} <span class="required-indicator">*</span></label>
                        <input type="text" id="productCategory" value="${esc(this.formData.productCategory)}" placeholder="e.g., SaaS, Consulting, Coaching" required>
                        <span class="field-error" id="error-productCategory"></span>
                    </div>

                    <div class="form-group">
                        <label for="pricePoint">Price Point <span class="required-indicator">*</span></label>
                        <input type="number" id="pricePoint" value="${esc(this.formData.pricePoint)}" placeholder="0" required>
                        <span class="field-error" id="error-pricePoint"></span>
                        <div class="radio-group" data-required="true" style="margin-top: 0.5rem;">
                            <label><input type="radio" name="purchaseType" value="one-time" ${this.formData.purchaseType === 'one-time' ? 'checked' : ''}> One-time purchase</label>
                            <label><input type="radio" name="purchaseType" value="subscription" ${this.formData.purchaseType === 'subscription' ? 'checked' : ''}> Subscription/Recurring</label>
                            <label><input type="radio" name="purchaseType" value="contract" ${this.formData.purchaseType === 'contract' ? 'checked' : ''}> High-commitment service</label>
                        </div>
                        <span class="field-error" id="error-purchaseType"></span>
                    </div>

                    <div class="form-group">
                        <fieldset class="radio-group" data-required="true">
                            <legend>Is there a deal on it right now? <span class="required-indicator">*</span></legend>
                            <label><input type="radio" name="offerHasDeal" value="yes" ${this.formData.offerHasDeal === 'yes' ? 'checked' : ''}> Yes</label>
                            <label><input type="radio" name="offerHasDeal" value="no" ${this.formData.offerHasDeal === 'no' ? 'checked' : ''}> No</label>
                        </fieldset>
                        <span class="field-error" id="error-offerHasDeal"></span>
                    </div>
                    ${this.formData.offerHasDeal === 'yes' ? `
                    <div class="form-group">
                        <label for="offerNormalPrice">Normal price <span class="required-indicator">*</span></label>
                        <input type="text" id="offerNormalPrice" value="${esc(this.formData.offerNormalPrice)}" required>
                        <span class="field-error" id="error-offerNormalPrice"></span>
                    </div>
                    <div class="form-group">
                        <label for="offerPrice">Offer price <span class="required-indicator">*</span></label>
                        <input type="text" id="offerPrice" value="${esc(this.formData.offerPrice)}" required>
                        <span class="field-error" id="error-offerPrice"></span>
                    </div>
                    <div class="form-group">
                        <label for="offerEnds">Ends on <span class="required-indicator">*</span></label>
                        <input type="text" id="offerEnds" value="${esc(this.formData.offerEnds)}" required placeholder="Friday">
                        <span class="field-error" id="error-offerEnds"></span>
                    </div>` : ''}
                </div>
            `;
        }

        renderSection2() {
            return `
                <div class="form-section">
                    <h2>Audience</h2>

                    <div class="form-group">
                        <label for="targetBuyer">Who is your ideal customer/client? <span class="required-indicator">*</span></label>
                        <textarea id="targetBuyer" rows="3" required data-min-chars="150" placeholder="B2B SaaS founders running teams of 10-30 people, bootstrapped or seed-stage, who feel stuck doing all the sales calls themselves instead of building a team.">${esc(this.formData.targetBuyer)}</textarea>
                        <small>Describe them in your own words, like you'd tell a colleague</small>
                        <span class="field-error" id="error-targetBuyer"></span>
                        <span class="min-chars-warning" id="warn-targetBuyer"></span>
                    </div>

                    <div class="form-group">
                        <label>Where are they in their awareness journey? <span class="required-indicator">*</span></label>
                        <div class="radio-group" data-required="true">
                            <label><input type="radio" name="awarenessLevel" value="unaware" ${this.formData.awarenessLevel === 'unaware' ? 'checked' : ''}> Unaware (don't realize they have this problem)</label>
                            <label><input type="radio" name="awarenessLevel" value="problem-aware" ${this.formData.awarenessLevel === 'problem-aware' ? 'checked' : ''}> Problem-Aware (know the pain, not solutions)</label>
                            <label><input type="radio" name="awarenessLevel" value="solution-aware" ${this.formData.awarenessLevel === 'solution-aware' ? 'checked' : ''}> Solution-Aware (comparing options)</label>
                            <label><input type="radio" name="awarenessLevel" value="product-aware" ${this.formData.awarenessLevel === 'product-aware' ? 'checked' : ''}> Product-Aware (know about you, deciding)</label>
                            <label><input type="radio" name="awarenessLevel" value="most-aware" ${this.formData.awarenessLevel === 'most-aware' ? 'checked' : ''}> Most Aware (ready to buy)</label>
                        </div>
                        <span class="field-error" id="error-awarenessLevel"></span>
                    </div>

                    <div class="form-group">
                        <label>What drives their purchasing decisions? <span class="required-indicator">*</span></label>
                        <div class="radio-group" data-required="true">
                            <label><input type="radio" name="buyerPsychology" value="analytical" ${this.formData.buyerPsychology === 'analytical' ? 'checked' : ''}> Analytical/Data-Driven (needs proof, metrics)</label>
                            <label><input type="radio" name="buyerPsychology" value="emotional" ${this.formData.buyerPsychology === 'emotional' ? 'checked' : ''}> Emotional/Transformation (wants to visualize outcome)</label>
                            <label><input type="radio" name="buyerPsychology" value="story-driven" ${this.formData.buyerPsychology === 'story-driven' ? 'checked' : ''}> Story-Driven (needs relatable narrative)</label>
                            <label><input type="radio" name="buyerPsychology" value="authority-seeking" ${this.formData.buyerPsychology === 'authority-seeking' ? 'checked' : ''}> Authority-Seeking (values credentials, studies)</label>
                            <label><input type="radio" name="buyerPsychology" value="risk-averse" ${this.formData.buyerPsychology === 'risk-averse' ? 'checked' : ''}> Risk-Averse (needs guarantees, reassurance)</label>
                        </div>
                        <span class="field-error" id="error-buyerPsychology"></span>
                    </div>

                    <div class="form-group">
                        <label>How quickly do they typically decide? <span class="required-indicator">*</span></label>
                        <div class="radio-group" data-required="true">
                            <label><input type="radio" name="purchaseTimeline" value="immediate" ${this.formData.purchaseTimeline === 'immediate' ? 'checked' : ''}> Immediate (impulse buy, minutes)</label>
                            <label><input type="radio" name="purchaseTimeline" value="short" ${this.formData.purchaseTimeline === 'short' ? 'checked' : ''}> Short (hours to days)</label>
                            <label><input type="radio" name="purchaseTimeline" value="medium" ${this.formData.purchaseTimeline === 'medium' ? 'checked' : ''}> Medium (1-2 weeks)</label>
                            <label><input type="radio" name="purchaseTimeline" value="long" ${this.formData.purchaseTimeline === 'long' ? 'checked' : ''}> Long (weeks to months)</label>
                            <label><input type="radio" name="purchaseTimeline" value="nurture" ${this.formData.purchaseTimeline === 'nurture' ? 'checked' : ''}> Ongoing Nurture (relationship-building)</label>
                        </div>
                        <span class="field-error" id="error-purchaseTimeline"></span>
                    </div>

                    <div class="form-group">
                        <label for="triggerEvents">What happens right before someone becomes ready to solve this? <span class="optional">(optional)</span></label>
                        <textarea id="triggerEvents" rows="2" placeholder="e.g., Lost a key client, got passed over for promotion, team member quit">${esc(this.formData.triggerEvents)}</textarea>
                        <small>Understanding trigger events helps personalize cold outreach</small>
                    </div>

                    <div class="form-group">
                        <label for="failedApproaches">What have prospects typically tried before finding you? <span class="optional">(optional)</span></label>
                        <textarea id="failedApproaches" rows="2" placeholder="e.g., Hired a VA, bought another course, tried competitor's tool">${esc(this.formData.failedApproaches)}</textarea>
                        <small>Knowing failed approaches helps differentiate your solution</small>
                    </div>
                </div>
            `;
        }

        renderSection3() {
            return `
                <div class="form-section">
                    <h2>Content & Messaging</h2>

                    <!-- OWNER RULING 2026-08-29: the pain question and its cost are asked on ALL FIVE
                         types. They were hidden from an event and an announcement, which still saw the
                         heading and its example with no box to answer in. Neither blocks completion. -->
                    <div class="form-group">
                        <label>Main Pain Points</label>
                        <textarea id="painPoint1" rows="2" data-min-chars="100" placeholder="I'm losing 2 hours a day chasing clients for content approvals instead of building the product.">${esc(this.formData.painPoints[0])}</textarea>
                        <small>Say it in their words — exactly how they'd describe it</small>
                        <span class="min-chars-warning" id="warn-painPoint1"></span>
                        <textarea id="painPoint2" rows="2" placeholder="Pain point 2 (optional)">${esc(this.formData.painPoints[1])}</textarea>
                        <textarea id="painPoint3" rows="2" placeholder="Pain point 3 (optional)">${esc(this.formData.painPoints[2])}</textarea>
                        <span class="field-error" id="error-painPoints"></span>
                    </div>

                    <div class="form-group">
                        <label for="problemCost">What is that costing them right now? <span class="optional">(optional)</span></label>
                        <input type="text" id="problemCost" value="${esc(this.formData.problemCost)}" placeholder="e.g., about 680 a month across their tools">
                        <fieldset class="radio-group">
                            <legend>Per week, per month, or per year?</legend>
                            <label><input type="radio" name="problemCostPeriod" value="Per week" ${this.formData.problemCostPeriod === 'Per week' ? 'checked' : ''}> Per week</label>
                            <label><input type="radio" name="problemCostPeriod" value="Per month" ${this.formData.problemCostPeriod === 'Per month' ? 'checked' : ''}> Per month</label>
                            <label><input type="radio" name="problemCostPeriod" value="Per year" ${this.formData.problemCostPeriod === 'Per year' ? 'checked' : ''}> Per year</label>
                        </fieldset>
                     </div>

                    <div class="form-group">
                        <label>Key Benefits <span class="required-indicator">*</span></label>
                        <input type="text" id="benefit1" required placeholder="Benefit 1 (required)" value="${esc(this.formData.keyBenefits[0])}">
                        <input type="text" id="benefit2" placeholder="Benefit 2 (optional)" value="${esc(this.formData.keyBenefits[1])}">
                        <input type="text" id="benefit3" placeholder="Benefit 3 (optional)" value="${esc(this.formData.keyBenefits[2])}">
                        <span class="field-error" id="error-keyBenefits"></span>
                    </div>

                    <div class="form-group">
                        <label for="uniqueValue">Unique Value Proposition <span class="required-indicator">*</span></label>
                        <textarea id="uniqueValue" rows="3" ${this.formData.promotionType === 'news' ? '' : 'required'} data-min-chars="150" placeholder="We onboard new clients in under an hour using a template library that every competitor makes you build from scratch. Most agencies spend 2 weeks on this.">${esc(this.formData.uniqueValue)}</textarea>
                        <small>In your own words, as if a prospect just asked</small>
                        <span class="field-error" id="error-uniqueValue"></span>
                        <span class="min-chars-warning" id="warn-uniqueValue"></span>
                    </div>

                    <div class="form-group">
                        <label for="enemyName">Named Enemy/Trap <span class="optional">(optional)</span></label>
                        <input type="text" id="enemyName" value="${esc(this.formData.enemyName)}" placeholder="e.g., The Meeting Overload Trap, The Hustle Harder Myth">
                        <small>The trap/enemy they're stuck in (not attacking them, but the system failing them)</small>
                    </div>

                    <div class="form-group">
                        <label for="prospectLanguage">Prospect's Own Language <span class="optional">(optional)</span></label>
                        <textarea id="prospectLanguage" rows="2" placeholder="e.g., 'I'm drowning in Slack messages' not 'communication inefficiency'">${esc(this.formData.prospectLanguage)}</textarea>
                        <small>How they describe the problem when talking to peers (exact words)</small>
                    </div>

                    <div class="form-group">
                        <label>Available Proof Assets (select all that apply)</label>
                        <div class="checkbox-group">
                            <label><input type="checkbox" value="metrics" ${this.formData.proofAssets.includes('metrics') ? 'checked' : ''}> Specific metrics/data (revenue, leads, conversions)</label>
                            <label><input type="checkbox" value="dashboards" ${this.formData.proofAssets.includes('dashboards') ? 'checked' : ''}> Analytics dashboards or screenshots</label>
                            <label><input type="checkbox" value="stories" ${this.formData.proofAssets.includes('stories') ? 'checked' : ''}> Customer success stories with transformation</label>
                            <label><input type="checkbox" value="studies" ${this.formData.proofAssets.includes('studies') ? 'checked' : ''}> Scientific studies, research, authority endorsements</label>
                            <label><input type="checkbox" value="before-after" ${this.formData.proofAssets.includes('before-after') ? 'checked' : ''}> Before/after visuals or case studies</label>
                            <label><input type="checkbox" value="testimonials" ${this.formData.proofAssets.includes('testimonials') ? 'checked' : ''}> Testimonials with specific results</label>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="socialProof">Social Proof Summary <span class="required-indicator">*</span></label>
                        <textarea id="socialProof" rows="3" data-min-chars="200" placeholder="One client, a 12-person agency in Austin, went from 40 hours a week on onboarding to under 4 hours within 30 days. Same team, same clients, new workflow.">${esc(this.formData.socialProof)}</textarea>
                        <small>Tell a specific result as a short story</small>
                        <span class="field-error" id="error-socialProof"></span>
                        <span class="min-chars-warning" id="warn-socialProof"></span>
                    </div>

                    <div class="form-group">
                        <label for="proofNumber">Your strongest proof number <span class="optional">(optional)</span></label>
                        <input type="text" id="proofNumber" value="${esc(this.formData.proofNumber)}" placeholder="e.g., 11,000 sold, 2.4 percent returned">
                        <small>A number, held apart from the story, so ads can quote it</small>
                    </div>

                    ${this.formData.promotionType === 'product' ? `
                    <div class="form-group">
                        <label for="topObjection">The objection you hear most <span class="optional">(optional)</span></label>
                        <input type="text" id="topObjection" value="${esc(this.formData.topObjection)}" placeholder="e.g., they think it takes weeks to set up">
                        <small>Every owner knows theirs — the ads answer it before it is raised</small>
                    </div>` : ''}

                    <div class="form-group">
                        <label for="proofQuoteName">The customer behind the quote <span class="optional">(optional)</span></label>
                        <input type="text" id="proofQuoteName" value="${esc(this.formData.proofQuoteName)}" placeholder="First name, e.g., Marcus">
                        <input type="text" id="proofQuoteRole" value="${esc(this.formData.proofQuoteRole)}" placeholder="What they do, e.g., runs two delis">
                        <small>Named quotes carry weight; leave blank if they asked not to be named</small>
                    </div>
                </div>
            `;
        }

        renderSection4() {
            // Debug: Log contentType value on Section 4 render
            console.log('[DiscoveryForm renderSection4] contentType:', this.formData.contentType, '| isNexusMode:', this.isNexusMode);

            // NEXUS mode: Show binary warm/cold selector instead of 5-option trafficSource
            const contentTypeSelector = this.isNexusMode ? `
                    <div class="form-group nexus-content-type">
                        <div class="nexus-mode-notice" style="background: var(--color-primary-50); border: 1px solid var(--color-primary-200); border-radius: 0.5rem; padding: 0.75rem; margin-bottom: 1rem;">
                            <span style="color: var(--color-primary-700);">🔗 <strong>NEXUS Mode:</strong> Choose your content type</span>
                        </div>
                        <label>Content Type <span class="required-indicator">*</span></label>
                        <div class="radio-group content-type-selector" data-required="true" style="display: flex; gap: 1rem;">
                            <label class="content-type-option" style="flex: 1; padding: 1rem; border: 2px solid var(--border-primary); border-radius: 0.5rem; cursor: pointer; text-align: center; ${this.formData.contentType === 'cold' ? 'border-color: var(--color-primary-600); background: var(--color-primary-50);' : ''}">
                                <input type="radio" name="contentType" value="cold" ${this.formData.contentType === 'cold' ? 'checked' : ''} style="display: none;">
                                <div style="font-size: 2rem;">❄️</div>
                                <div style="font-weight: 600; margin: 0.5rem 0;">Cold Outreach</div>
                                <div style="font-size: 0.85rem; color: var(--text-secondary);">Reaching people who don't know you yet</div>
                            </label>
                            <label class="content-type-option" style="flex: 1; padding: 1rem; border: 2px solid var(--border-primary); border-radius: 0.5rem; cursor: pointer; text-align: center; ${this.formData.contentType === 'warm' ? 'border-color: var(--color-primary-600); background: var(--color-primary-50);' : ''}">
                                <input type="radio" name="contentType" value="warm" ${this.formData.contentType === 'warm' ? 'checked' : ''} style="display: none;">
                                <div style="font-size: 2rem;">🔥</div>
                                <div style="font-weight: 600; margin: 0.5rem 0;">Warm Audience</div>
                                <div style="font-size: 0.85rem; color: var(--text-secondary);">Reaching people who already know you</div>
                            </label>
                        </div>
                        <span class="field-error" id="error-contentType"></span>
                    </div>
            ` : '';

            // trafficSource selector removed - channel selection no longer affects framework recommendation
            // trafficSource is auto-set in NEXUS mode based on contentType (see saveCurrentSection)

            return `
                <div class="form-section">
                    <h2>Offer & Channel</h2>

                    ${contentTypeSelector}

                    <div class="form-group">
                        <label>What's the goal of this campaign? <span class="required-indicator">*</span></label>
                        <div class="radio-group" data-required="true">
                            <label><input type="radio" name="campaignGoal" value="direct-sales" ${this.formData.campaignGoal === 'direct-sales' ? 'checked' : ''}> Make direct sales</label>
                            <label><input type="radio" name="campaignGoal" value="book-calls" ${this.formData.campaignGoal === 'book-calls' ? 'checked' : ''}> Book calls / demos</label>
                            <label><input type="radio" name="campaignGoal" value="fill-event" ${this.formData.campaignGoal === 'fill-event' ? 'checked' : ''}> Fill an event</label>
                            <label><input type="radio" name="campaignGoal" value="launch" ${this.formData.campaignGoal === 'launch' ? 'checked' : ''}> Launch something new</label>
                            <label><input type="radio" name="campaignGoal" value="collect-leads" ${this.formData.campaignGoal === 'collect-leads' ? 'checked' : ''}> Collect leads</label>
                            <label><input type="radio" name="campaignGoal" value="build-authority" ${this.formData.campaignGoal === 'build-authority' ? 'checked' : ''}> Build authority</label>
                        </div>
                        <small>This shapes which framework we recommend and how the copy is pitched.</small>
                        <span class="field-error" id="error-campaignGoal"></span>
                    </div>

                    <div class="form-group">
                        <label for="offerDetails">Offer Details <span class="required-indicator">*</span></label>
                        <textarea id="offerDetails" rows="4" ${this.formData.promotionType === 'news' ? '' : 'required'} data-min-chars="200" placeholder="A 30-minute audit call where we review your onboarding flow and hand you a written playbook tailored to your agency. No cost, no pitch. Walk away with the plan either way.">${esc(this.formData.offerDetails)}</textarea>
                        <small>In your own words, how you'd pitch it on a call</small>
                        <span class="field-error" id="error-offerDetails"></span>
                        <span class="min-chars-warning" id="warn-offerDetails"></span>
                    </div>

                    <div class="form-group">
                        <label for="callToAction">Call-to-Action <span class="required-indicator">*</span></label>
                        <input type="text" id="callToAction" required value="${esc(this.formData.callToAction)}" placeholder="e.g., Book a Discovery Call, Buy Now, Start Free Trial">
                        <span class="field-error" id="error-callToAction"></span>
                    </div>

                    <div class="form-group">
                        <label>Where will you send them? <span class="required-indicator">*</span></label>
                        <div class="radio-group" data-required="true">
                            <label><input type="radio" name="conversionDestination" value="landing-page" ${this.formData.conversionDestination === 'landing-page' ? 'checked' : ''}> Landing page</label>
                            <label><input type="radio" name="conversionDestination" value="vsl" ${this.formData.conversionDestination === 'vsl' ? 'checked' : ''}> Video sales letter</label>
                            <label><input type="radio" name="conversionDestination" value="direct-booking" ${this.formData.conversionDestination === 'direct-booking' ? 'checked' : ''}> Direct booking/calendar</label>
                            <label><input type="radio" name="conversionDestination" value="lead-magnet" ${this.formData.conversionDestination === 'lead-magnet' ? 'checked' : ''}> Lead magnet download</label>
                        </div>
                        <span class="field-error" id="error-conversionDestination"></span>
                    </div>

                    <div class="form-group">
                        <label for="actionUrl">And where does it take them? <span class="optional">(optional)</span></label>
                        <input type="url" id="actionUrl" value="${esc(this.formData.actionUrl)}" placeholder="https://your-site.example/book">
                        <small>The full https address the click lands on</small>
                    </div>
                </div>
            `;
        }

        // MP-02: Brand Voice tab — 5th tab, owner mode only.
        // Pre-fills with /voice-starter (Ollama in prod, Claude in dev) on first visit.
        // User edits freely. Continue saves whatever is in the textarea.
        renderSection5() {
            // The saved voice line decomposes exactly as the conversation does: segments matching a
            // tone tick it; everything else preloads the freehand. Nothing written is ever dropped.
            const savedParts = (this.formData.brandTones || '').split(',').map((x) => x.trim()).filter(Boolean);
            const ticked = savedParts.filter((x) => TONES.includes(x));
            const more = savedParts.filter((x) => !TONES.includes(x)).join(', ');
            const val = Number.isInteger(this.formData.adIntensity) ? this.formData.adIntensity : 3;
            return `
                <div class="form-section">
                    <h2>Tone</h2>
                    <p class="section-subtitle">How hard your ads push, and how they sound.</p>

                    <div class="form-group">
                        <label for="adIntensity">How hard should your ads push? <span class="required-indicator">*</span></label>
                        <div class="scale-labels" aria-hidden="true">
                            ${[1, 2, 3, 4, 5].map((n) => `<span class="scale-label${n === val ? ' on' : ''}"><b>${n}</b>${esc(INTENSITY_STOPS[n].split(' - ')[0])}</span>`).join('')}
                        </div>
                        <input type="range" id="adIntensity" class="scale-range" min="1" max="5" step="1" value="${val}"
                               list="intensity-marks" aria-describedby="adIntensity-stop">
                        <datalist id="intensity-marks">${[1, 2, 3, 4, 5].map((n) => `<option value="${n}"></option>`).join('')}</datalist>
                        <span class="count" id="adIntensity-stop" aria-live="polite">${esc(INTENSITY_STOPS[val])}</span>
                    </div>

                    <div class="form-group">
                        <fieldset class="radio-group tones-grid" data-required="true">
                            <legend>How should your ads sound? Pick at least one, or write your own. <span class="required-indicator">*</span></legend>
                            ${TONES.map((t) => `<label><input type="checkbox" name="brandVoiceTones" value="${esc(t)}" ${ticked.includes(t) ? 'checked' : ''}> ${esc(t)}</label>`).join('\n                            ')}
                        </fieldset>
                        <label for="brandVoiceMore">More, in your own words</label>
                        <textarea id="brandVoiceMore" rows="3">${esc(more)}</textarea>
                        <span class="field-error" id="error-brandVoice"></span>
                    </div>
                </div>
            `;
        }

        // Brand Voice — its OWN tab, restored exactly as it was (founder ruling 2026-08-05):
        // the editable voice text, its Regenerate button, and the auto-generated starter on entry.
        renderSection6() {
            const voiceText = this.formData.brandVoice || '';
            return `
                <div class="form-section brand-voice-tab">
                    <h2>Brand voice</h2>
                    <p class="section-subtitle">This is how the AI will write on your behalf. Edit it until it sounds like you. What's here when you continue becomes your voice.</p>

                    <div class="form-group">
                        <label for="brandVoice">Your brand voice <span class="required-indicator">*</span></label>
                        <textarea id="brandVoice" rows="8" required placeholder="Loading starter draft from your answers...">${esc(voiceText)}</textarea>
                        <small>The starter draft is generated from your answers. Edit freely — short sentences, your words, your style.</small>
                        <span class="field-error" id="error-brandVoice"></span>
                        <div class="brand-voice-actions">
                            <button type="button" id="brand-voice-regenerate-btn" class="btn-secondary"${this.viewOnly ? ' disabled' : ''}>↻ Regenerate</button>
                            <span id="brand-voice-status" class="brand-voice-status"></span>
                        </div>
                    </div>
                </div>
            `;
        }

        // MP-02: Fetch a fresh voice-starter from the backend and place it in the freehand box
        // (positioning MP-02: the long editor became the "More, in your own words" area; the
        // generated starter lands there and the person's ticks stay untouched).
        async fetchVoiceStarter() {
            const textarea = document.getElementById('brandVoice');
            const statusEl = document.getElementById('brand-voice-status');
            // Founder ruling 2026-08-05: no generation while any mandatory answer is missing — the
            // voice would be written from half the facts. The blocker is named, using the same
            // rule the Continue button uses, and shown in the existing error line.
            const blocker = this.firstIncompleteSection ? this.firstIncompleteSection() : null;
            if (blocker && blocker.num !== 6) {
                const err = document.getElementById('error-brandVoice');
                const words = `Finish the "${blocker.name}" tab first — your voice is written from your answers.`;
                if (err) { err.textContent = words; err.style.display = 'block'; }
                if (statusEl) { statusEl.textContent = words; statusEl.style.color = 'var(--color-error-600)'; }
                return;
            }
            const errClear = document.getElementById('error-brandVoice');
            if (errClear) { errClear.textContent = ''; errClear.style.display = 'none'; }
            const status = document.getElementById('brand-voice-status');
            if (!textarea) return;
            if (status) {
                status.textContent = 'Generating starter…';
                status.style.color = 'var(--text-tertiary)';
            }
            try {
                const apiBase = window.CAMPAIGNS_CONFIG.DISCOVERY_API_URL;
                // Authenticated request — voice-starter requires JWT (NEXUS or local AuthService).
                // Headers are built per attempt so the 401-retry picks up a fresh token.
                const buildHeaders = () => {
                    const headers = { 'Content-Type': 'application/json' };
                    const nexusToken = sessionStorage.getItem('nexus_access_token');
                    if (nexusToken) {
                        headers.Authorization = `Bearer ${nexusToken}`;
                    } else if (window.AuthService && typeof window.AuthService.getAuthHeaders === 'function') {
                        const auth = window.AuthService.getAuthHeaders();
                        if (auth.Authorization) headers.Authorization = auth.Authorization;
                    }
                    return headers;
                };
                const response = await NexusConfig.fetchWithAuthRetry(() => fetch(`${apiBase}/voice-starter`, {
                    method: 'POST',
                    headers: buildHeaders(),
                    // The whole answer set travels, so the voice reflects the tones picked, the push
                    // level, the sentence, and the cost with its timeframe — not just the old fields.
                    body: JSON.stringify({ discoveryData: this.formData })
                }));
                if (!response.ok) {
                    const errBody = await response.text();
                    throw new Error(`HTTP ${response.status}: ${errBody.substring(0, 150)}`);
                }
                const data = await response.json();
                const starter = (data.starter || '').trim();
                if (!starter) throw new Error('Empty starter returned');
                textarea.value = starter;
                this.formData.brandVoice = starter;
                await this.saveDraft();
                this.updateButtonState();
                if (status) {
                    status.textContent = `Starter generated via ${data.backend || 'AI'}.`;
                    status.style.color = 'var(--text-tertiary)';
                }
            } catch (err) {
                console.error('[Brand Voice] starter fetch failed:', err);
                if (status) {
                    const detail = err && err.message ? err.message : 'unknown error';
                    status.textContent = `Could not generate starter (${detail}). Please type your voice in your own words.`;
                    status.style.color = 'var(--color-error)';
                }
            }
        }

        attachEventListeners() {
            // MP-07: the choice and the deal answer decide which questions exist — save, then re-render,
            // the same save-then-render pattern the tabs use, so typed values survive the swap.
            // positioning MP-02: the slider's stop is stated in words as it moves
            const intensitySlider = document.getElementById('adIntensity');
            if (intensitySlider) {
                intensitySlider.addEventListener('input', () => {
                    const stop = document.getElementById('adIntensity-stop');
                    if (stop) stop.textContent = INTENSITY_STOPS[intensitySlider.value] || '';
                    document.querySelectorAll('.scale-label').forEach((el, i) => {
                        el.classList.toggle('on', String(i + 1) === String(intensitySlider.value));
                    });
                });
            }

            document.querySelectorAll('input[name="promotionType"], input[name="offerHasDeal"]').forEach(radio => {
                radio.addEventListener('change', () => {
                    this.saveCurrentSection();
                    this.render();
                });
            });

            // Section tabs
            document.querySelectorAll('.section-tab').forEach(tab => {
                tab.addEventListener('click', () => {
                    const sectionNum = parseInt(tab.dataset.section);
                    this.saveCurrentSection();
                    this.currentSection = sectionNum;
                    this.render();
                });
            });

            // Navigation buttons
            const prevBtn = document.getElementById('prev-btn');
            const nextBtn = document.getElementById('next-btn');
            const submitBtn = document.getElementById('submit-btn');

            if (prevBtn) {
                prevBtn.addEventListener('click', () => {
                    this.saveCurrentSection();
                    this.currentSection--;
                    this.render();
                });
            }

            if (nextBtn) {
                nextBtn.addEventListener('click', () => {
                    if (this.validateCurrentSection()) {
                        this.saveCurrentSection();
                        this.currentSection++;
                        this.render();
                    }
                });
            }

            // The hand-off failed and the person is looking at an error: give the button back, so the
            // only thing stopping them trying again is the problem itself.
            if (!this._failureWatch) {
                this._failureWatch = () => this.setSubmitWorking(false);
                window.addEventListener('discoveryHandoffFailed', this._failureWatch);
            }

            if (submitBtn) {
                submitBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (this.validateCurrentSection()) {
                        // SAY THAT IT IS WORKING — founder request, 11 August 2026.
                        // Saving the answers and filing them takes a few seconds, and nothing on screen
                        // changed, so the button read as dead and people pressed it again.
                        this.setSubmitWorking(true);
                        this.saveCurrentSection();
                        this.submitForm();
                    }
                });
            }

            // Edit button (view mode only)
            const editBtn = document.getElementById('edit-btn');
            if (editBtn) {
                editBtn.addEventListener('click', () => {
                    this.navigateToEditMode();
                });
            }

            // Cancel/Back to Dashboard button (edit mode only)
            const cancelBtn = document.getElementById('cancel-btn');
            if (cancelBtn) {
                cancelBtn.addEventListener('click', () => {
                    this.navigateBackToDashboard();
                });
            }

            // Download/Upload buttons
            const downloadBtn = document.getElementById('download-discovery-btn');
            if (downloadBtn) {
                downloadBtn.addEventListener('click', () => this.downloadDiscovery());
            }
            const uploadBtn = document.getElementById('upload-discovery-btn');
            if (uploadBtn) {
                uploadBtn.addEventListener('click', () => {
                    document.getElementById('discovery-file-input')?.click();
                });
            }
            const fileInput = document.getElementById('discovery-file-input');
            if (fileInput) {
                fileInput.addEventListener('change', (e) => this.uploadDiscovery(e));
            }

            // Share Form button
            const shareBtn = document.getElementById('share-form-btn');
            if (shareBtn) {
                shareBtn.addEventListener('click', () => this.showShareFormDialog('discovery'));
            }
            // MP-11: Share as Teaser — same popup, only the created link points to the teaser.
            const teaserBtn = document.getElementById('share-teaser-btn');
            if (teaserBtn) {
                teaserBtn.addEventListener('click', () => this.showShareFormDialog('teaser'));
            }

            // Form inputs — saved when focus LEAVES a field, not on every keystroke.
            const form = document.getElementById('discovery-form');
            if (form) {
                // Typing only updates the buttons. It costs nothing and never touches the server.
                //
                // CHANGED 11 August 2026, founder ruling. Every keystroke used to send a full save —
                // a round trip per character. Typing a sentence issued a save per letter, and by the
                // time somebody pressed Continue a queue of identical saves was still draining, so
                // the screen sat for about ten seconds doing work that had already been done.
                form.addEventListener('input', () => {
                    this.updateButtonState();
                });

                form.addEventListener('blur', (e) => {
                    // Save what they just typed, now they have moved on from it.
                    this.saveCurrentSection();
                    this.updateButtonState();

                    // Min-chars warning (non-blocking soft nudge)
                    if (e.target && e.target.dataset && e.target.dataset.minChars) {
                        this.checkMinCharsWarning(e.target);
                    }
                }, true);

                // Leaving the page saves whatever is in the field they are still standing in.
                //
                // Saving on every keystroke had one accidental virtue: nothing could ever be lost.
                // Saving on blur alone would lose the last field if somebody typed into it and then
                // closed the tab or switched away without moving on. This closes that.
                //
                // "hidden" is the only event a browser guarantees on a closing or backgrounded tab —
                // beforeunload is not fired reliably on mobile.
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState === 'hidden') this.saveCurrentSection();
                });
            }

            // NEXUS mode: Update contentType visual indicator on click
            if (this.isNexusMode) {
                document.querySelectorAll('input[name="contentType"]').forEach(radio => {
                    radio.addEventListener('change', async () => {
                        console.log('[Discovery] contentType changed to:', radio.value);
                        console.log('[Discovery] Current campaignId:', this.campaignId);
                        console.log('[Discovery] formData before update:', JSON.stringify(this.formData));

                        this.formData.contentType = radio.value;
                        this.updateContentTypeVisuals(radio.value);

                        // Also save to discoveryFormData for Dashboard to read (cross-origin won't work but keep for compatibility)
                        localStorage.setItem('discoveryFormData', JSON.stringify(this.formData));

                        // CRITICAL: Await the server save to ensure contentType is persisted
                        try {
                            await this.saveDraft();
                            console.log('[Discovery] Server save completed for contentType:', radio.value);
                        } catch (e) {
                            console.error('[Discovery] Server save FAILED:', e);
                        }
                    });
                });
                // Set initial visual state from formData
                if (this.formData.contentType) {
                    this.updateContentTypeVisuals(this.formData.contentType);
                }
            }

            // Initial button state check
            this.updateButtonState();

            // MP-02: Brand Voice tab — wire regenerate and auto-fetch only in editable owner mode
            if (this.currentSection === 6 && !this.externalMode && !this.viewOnly) {
                const regenBtn = document.getElementById('brand-voice-regenerate-btn');
                if (regenBtn) {
                    regenBtn.addEventListener('click', () => this.fetchVoiceStarter());
                }
                // Auto-generate starter on first entry if textarea is empty
                if (!this.formData.brandVoice?.trim()) {
                    this.fetchVoiceStarter();
                }
            }
        }

        /**
         * Update contentType option visual styling
         */
        updateContentTypeVisuals(selectedValue) {
            document.querySelectorAll('.content-type-option').forEach(label => {
                const radio = label.querySelector('input[type="radio"]');
                if (radio && radio.value === selectedValue) {
                    label.style.borderColor = 'var(--color-primary-600)';
                    label.style.background = 'var(--color-primary-50)';
                } else {
                    label.style.borderColor = 'var(--border-primary)';
                    label.style.background = '';
                }
            });
        }

        saveCurrentSection() {
            // Viewing must never change what is stored. Every write path — the tab strip, Previous,
            // Next, download and the background saver — funnels through here, so this is the one
            // place that has to hold the line.
            if (this.viewOnly) return;
            const form = document.getElementById('discovery-form');
            if (!form) return;

            // Save based on current section
            switch (this.currentSection) {
                case 0:
                    this.formData.promotionType = form.promotionType?.value || '';
                    ['eventName', 'eventDate', 'eventStartTime', 'eventEndTime', 'eventLocation',
                     'eventHost', 'eventHostCredential', 'announcementSubject', 'announcementWhen',
                     // ads-brief-per-type-cutover step 5: each type's own answers
                     'announcementImpact', 'courseStarts', 'courseTeacher', 'serviceArea', 'serviceAvailability']
                        .forEach((k) => { const el = form.querySelector('#' + k); if (el) this.formData[k] = el.value || ''; });
                    if (form.eventFormat) this.formData.eventFormat = form.eventFormat.value || '';
                    break;
                case 1:
                    this.formData.productName = form.querySelector('#productName')?.value || '';
                    this.formData.productDescription = form.querySelector('#productDescription')?.value || '';
                    this.formData.websiteUrl = form.querySelector('#websiteUrl')?.value || '';
                    this.formData.linkedinUrl = form.querySelector('#linkedinUrl')?.value || '';
                    this.formData.productCategory = form.querySelector('#productCategory')?.value || '';
                    this.formData.pricePoint = form.querySelector('#pricePoint')?.value || '';
                    this.formData.purchaseType = form.purchaseType?.value || '';
                    this.formData.offerHasDeal = form.offerHasDeal?.value || '';
                    ['offerNormalPrice', 'offerPrice', 'offerEnds']
                        .forEach((k) => { const el = form.querySelector('#' + k); if (el) this.formData[k] = el.value || ''; });
                    break;
                case 2:
                    this.formData.targetBuyer = form.querySelector('#targetBuyer')?.value || '';
                    this.formData.awarenessLevel = form.awarenessLevel?.value || '';
                    this.formData.buyerPsychology = form.buyerPsychology?.value || '';
                    this.formData.purchaseTimeline = form.purchaseTimeline?.value || '';
                    this.formData.triggerEvents = form.querySelector('#triggerEvents')?.value || '';
                    this.formData.failedApproaches = form.querySelector('#failedApproaches')?.value || '';
                    break;
                case 3:
                    this.formData.painPoints = [
                        form.querySelector('#painPoint1')?.value || '',
                        form.querySelector('#painPoint2')?.value || '',
                        form.querySelector('#painPoint3')?.value || ''
                    ];
                    this.formData.keyBenefits = [
                        form.querySelector('#benefit1')?.value || '',
                        form.querySelector('#benefit2')?.value || '',
                        form.querySelector('#benefit3')?.value || ''
                    ];
                    this.formData.uniqueValue = form.querySelector('#uniqueValue')?.value || '';
                    this.formData.enemyName = form.querySelector('#enemyName')?.value || '';
                    this.formData.prospectLanguage = form.querySelector('#prospectLanguage')?.value || '';
                    this.formData.proofAssets = Array.from(form.querySelectorAll('input[type="checkbox"]:checked')).map(cb => cb.value);
                    this.formData.socialProof = form.querySelector('#socialProof')?.value || '';
                    ['problemCost', 'proofNumber', 'proofQuoteName', 'proofQuoteRole', 'topObjection']
                        .forEach((k) => { const el = form.querySelector('#' + k); if (el) this.formData[k] = el.value || ''; });
                    if (form.problemCostPeriod) this.formData.problemCostPeriod = form.problemCostPeriod.value || '';
                    break;
                case 4:
                    // NEXUS mode: save contentType and auto-set trafficSource
                    if (this.isNexusMode) {
                        this.formData.contentType = form.contentType?.value || '';
                        // Auto-set trafficSource based on contentType
                        if (this.formData.contentType === 'cold') {
                            this.formData.trafficSource = 'cold-linkedin'; // Default cold source
                        } else if (this.formData.contentType === 'warm') {
                            this.formData.trafficSource = 'organic'; // Default warm source
                        }
                    } else {
                        this.formData.trafficSource = form.trafficSource?.value || '';
                    }
                    this.formData.campaignGoal = form.campaignGoal?.value || '';
                    this.formData.offerDetails = form.querySelector('#offerDetails')?.value || '';
                    this.formData.callToAction = form.querySelector('#callToAction')?.value || '';
                    this.formData.conversionDestination = form.conversionDestination?.value || '';
                    this.formData.actionUrl = form.querySelector('#actionUrl')?.value || '';
                    break;
                case 5: {
                    // The Tone tab: ticks compose comma-joined (screen order), freehand appends.
                    // These are INPUT to the brand voice — they never overwrite it.
                    const ticked = [].slice.call(form.querySelectorAll('input[name="brandVoiceTones"]:checked')).map((c) => c.value);
                    const more = form.querySelector('#brandVoiceMore')?.value.trim();
                    if (more) ticked.push(more);
                    this.formData.brandTones = ticked.join(', ');
                    const slider = form.querySelector('#adIntensity');
                    if (slider) this.formData.adIntensity = Number(slider.value); // a NUMBER end to end
                    break;
                }
                case 6:
                    // Brand Voice: its own tab, its own column, exactly as before.
                    this.formData.brandVoice = form.querySelector('#brandVoice')?.value || '';
                    break;
            }

            return this.saveDraft();
        }

        checkMinCharsWarning(el) {
            const min = parseInt(el.dataset.minChars, 10);
            if (!min) return;
            const len = (el.value || '').trim().length;
            const warnSpan = document.getElementById(`warn-${el.id}`);
            if (!warnSpan) return;
            if (len > 0 && len < min) {
                warnSpan.textContent = `Short answer (${len}/${min} chars). Fuller answers help the AI match your voice.`;
                warnSpan.style.display = 'block';
            } else {
                warnSpan.textContent = '';
                warnSpan.style.display = 'none';
            }
        }

        validateCurrentSection() {
            const form = document.getElementById('discovery-form');
            let valid = true;

            // Clear all previous errors
            form.querySelectorAll('.field-error').forEach(error => {
                error.textContent = '';
                error.style.display = 'none';
            });
            form.querySelectorAll('.error').forEach(el => el.classList.remove('error'));

            // Validate text inputs and textareas
            const inputs = form.querySelectorAll('[required]');
            inputs.forEach(input => {
                if (!input.value.trim()) {
                    input.classList.add('error');
                    const errorSpan = document.getElementById(`error-${input.id}`);
                    if (errorSpan) {
                        errorSpan.textContent = 'This field is required';
                        errorSpan.style.display = 'block';
                    }
                    valid = false;
                } else {
                    input.classList.remove('error');
                }
            });

            // Validate radio groups (include contentType in NEXUS mode)
            const radioGroups = ['promotionType', 'eventFormat', 'purchaseType', 'offerHasDeal', 'awarenessLevel', 'buyerPsychology', 'purchaseTimeline', 'conversionDestination', 'contentType'];
            radioGroups.forEach(groupName => {
                const radios = form.querySelectorAll(`input[name="${groupName}"]`);
                if (radios.length > 0 && this.isRadioGroupInCurrentSection(groupName)) {
                    const checked = Array.from(radios).some(r => r.checked);
                    const radioGroup = radios[0].closest('.radio-group');
                    if (!checked) {
                        valid = false;
                        radioGroup.classList.add('error');
                        const errorSpan = document.getElementById(`error-${groupName}`);
                        if (errorSpan) {
                            errorSpan.textContent = 'Please select an option';
                            errorSpan.style.display = 'block';
                        }
                    } else {
                        radioGroup?.classList.remove('error');
                    }
                }
            });

            return valid;
        }

        isRadioGroupInCurrentSection(groupName) {
            // NEXUS mode: section 4 has contentType + conversionDestination
            // Standard mode: section 4 only has conversionDestination (trafficSource removed)
            const section4Groups = this.isNexusMode
                ? ['contentType', 'conversionDestination']
                : ['conversionDestination'];

            const sectionMap = {
                0: ['promotionType', 'eventFormat'],
                1: ['purchaseType', 'offerHasDeal'],
                2: ['awarenessLevel', 'buyerPsychology', 'purchaseTimeline'],
                4: section4Groups
            };
            return sectionMap[this.currentSection]?.includes(groupName);
        }

        updateButtonState() {
            const nextBtn = document.getElementById('next-btn');
            const submitBtn = document.getElementById('submit-btn');

            // Update Next button based on current section completion
            if (nextBtn) {
                const isCurrentSectionValid = this.checkSectionCompletion();
                if (isCurrentSectionValid) {
                    nextBtn.disabled = false;
                    nextBtn.classList.remove('btn-disabled');
                } else {
                    nextBtn.disabled = true;
                    nextBtn.classList.add('btn-disabled');
                }
            }

            // Update Submit button based on all REQUIRED fields (not optional ones like proofAssets)
            if (submitBtn) {
                const missing = this.firstIncompleteSection();
                const hint = document.getElementById('submit-hint');
                if (!missing) {
                    submitBtn.disabled = false;
                    submitBtn.classList.remove('btn-disabled');
                    if (hint) hint.style.display = 'none';
                } else {
                    submitBtn.disabled = true;
                    submitBtn.classList.add('btn-disabled');
                    if (hint) {
                        hint.textContent = `Still needed: an answer on the "${missing.name}" tab.`;
                        hint.style.display = 'block';
                    }
                }
            }
        }

        /**
         * Check if all REQUIRED fields are complete (excludes optional fields like proofAssets)
         */
        /**
         * The first section whose REQUIRED answers are missing, by tab name — or null when
         * everything required is answered. The submit gate and its on-screen hint both read THIS,
         * so the button can never be dead without the reason being nameable.
         *
         * MOVED 11 August 2026, founder ruling: the rule itself now lives beside the five campaign
         * types, where the SERVER can read it too. The copy asked a different question and refused a
         * finished discovery. This is the same rule, called rather than held.
         */
        firstIncompleteSection() {
            return firstIncompleteSection(this.formData, {
                isNexusMode: this.isNexusMode,
                externalMode: this.externalMode,
            });
        }

        areAllRequiredFieldsComplete() {
            return this.firstIncompleteSection() === null;
        }

        checkSectionCompletion() {
            const form = document.getElementById('discovery-form');
            if (!form) return false;

            // Check text inputs and textareas
            const inputs = form.querySelectorAll('[required]');
            for (const input of inputs) {
                if (!input.value.trim()) {
                    return false;
                }
            }

            // Check radio groups (include contentType for NEXUS mode)
            const radioGroups = ['promotionType', 'eventFormat', 'purchaseType', 'offerHasDeal', 'awarenessLevel', 'buyerPsychology', 'purchaseTimeline', 'conversionDestination', 'contentType'];
            for (const groupName of radioGroups) {
                if (this.isRadioGroupInCurrentSection(groupName)) {
                    const radios = form.querySelectorAll(`input[name="${groupName}"]`);
                    if (radios.length > 0) {
                        const checked = Array.from(radios).some(r => r.checked);
                        if (!checked) {
                            return false;
                        }
                    }
                }
            }

            return true;
        }

        /**
         * Show, or stop showing, that the submit control is working.
         *
         * Added 11 August 2026. The same spinner the generation screen uses, scaled into the button, with
         * the label saying what is actually happening rather than a bare "please wait".
         *
         * It is switched OFF again by the failure signal below — never by a timer. A timer would hand the
         * button back while the save was still running, and somebody would press it twice.
         */
        setSubmitWorking(working) {
            const btn = document.getElementById('submit-btn');
            if (!btn) return;
            if (working) {
                if (!this._submitLabel) this._submitLabel = btn.innerHTML;
                const doing = this.externalMode ? 'Submitting' : 'Saving';
                btn.disabled = true;
                btn.classList.add('btn-working');
                btn.innerHTML = `<span class="btn-working-spinner" aria-hidden="true"></span>${doing}\u2026`;
                btn.setAttribute('aria-busy', 'true');
            } else {
                btn.disabled = false;
                btn.classList.remove('btn-working');
                if (this._submitLabel) btn.innerHTML = this._submitLabel;
                btn.removeAttribute('aria-busy');
            }
        }

        submitForm() {
            // In viewOnly mode, go back to dashboard
            if (this.viewOnly) {
                NexusConfig.navigateTo('/tools/dashboard/web/multi-framework-dashboard.html',
                    { campaignId: this.campaignId });
                return;
            }

            console.log('Form submitted:', this.formData);
            // Trigger framework scoring (MP-1.2) or update existing campaign
            window.dispatchEvent(new CustomEvent('discoveryComplete', {
                detail: {
                    formData: this.formData,
                    campaignId: this.campaignId,
                    editMode: this.editMode
                }
            }));
            this.clearDraft();
        }

        /**
         * Navigate to edit mode (from view mode)
         * Preserves NEXUS mode context
         */
        navigateToEditMode() {
            NexusConfig.navigateTo('/tools/discovery/web/campaign-discovery-chat.html',
                { edit: this.campaignId, forceEdit: 'true' });
        }

        /**
         * Navigate back to dashboard (cancel edit mode)
         * Preserves NEXUS mode context
         */
        navigateBackToDashboard() {
            NexusConfig.navigateTo('/tools/dashboard/web/multi-framework-dashboard.html',
                { campaignId: this.campaignId });
        }

        /**
         * Save draft to localStorage and server (if brandName exists)
         * Server save enables cross-device access
         */
        async saveDraft() {
            // The write itself, guarded as well as its callers: viewing changes nothing, whichever
            // route asks for the save.
            if (this.viewOnly) return { ok: true };
            // Always save to localStorage (offline backup)
            localStorage.setItem(this.draftKey, JSON.stringify(this.formData));

            // Skip server sync in external mode (prospect has no auth)
            if (this.externalMode) {
                const localOnly = { ok: true };
                this.showAutosaveIndicator(localOnly);
                return localOnly;
            }

            // Sync to server if brandName exists (required for server save)
            const brandName = this.formData.brandName || this.formData.productName;
            console.log('[Discovery saveDraft] brandName check:', brandName, '| campaignId:', this.campaignId);
            console.log('[Discovery saveDraft] contentType:', this.formData.contentType);
            if (brandName && brandName.trim()) {
                try {
                    // Get API base URL from config
                    const apiBase = window.CAMPAIGNS_CONFIG.DISCOVERY_API_URL;

                    const repository = new DiscoveryRepository(apiBase);

                    // Ensure brandName is set for server
                    const dataToSave = { ...this.formData };
                    if (!dataToSave.brandName && dataToSave.productName) {
                        dataToSave.brandName = dataToSave.productName;
                    }

                    // CRITICAL: Ensure campaignId is included to prevent duplicates
                    if (this.campaignId && !dataToSave.campaignId) {
                        dataToSave.campaignId = this.campaignId;
                    }

                    const result = await repository.saveCampaignDraft(dataToSave, this.nexusId || null);

                    // Store campaignId for future saves
                    if (result.campaignId && !this.formData.campaignId) {
                        this.formData.campaignId = result.campaignId;
                        // Update localStorage with campaignId
                        localStorage.setItem(this.draftKey, JSON.stringify(this.formData));
                    }

                    console.log(`Draft synced to server: ${result.completionPercent}% complete`);

                    const saved = { ok: true };
                    this.showAutosaveIndicator(saved);
                    return saved;

                } catch (error) {
                    // The browser copy is a backup, not a save. The person is TOLD, and the message
                    // stays on screen — a refused save that reported "saved" is how work was lost.
                    console.error('Server sync failed (localStorage backup active):', error.message);
                    const failed = {
                        ok: false,
                        message: `Not saved to your account — ${error.message}. Your answers are only in this browser, so keep this tab open.`
                    };
                    this.showAutosaveIndicator(failed);
                    return failed;
                }
            } else {
                console.warn('[Discovery saveDraft] SKIPPED server save - no brandName/productName');
                // Nothing can reach the account without a name yet. Say what is true, not "saved".
                const browserOnly = { ok: true, message: 'Saved in this browser' };
                this.showAutosaveIndicator(browserOnly);
                return browserOnly;
            }
        }

        /**
         * Download current discovery form data as a JSON file
         */
        downloadDiscovery() {
            this.saveCurrentSection();
            const data = {
                ...this.formData,
                _exportMeta: {
                    exportedAt: new Date().toISOString(),
                    campaignId: this.campaignId,
                    source: 'discovery-form'
                }
            };

            const productName = this.formData.productName || this.formData.brandName || 'discovery';
            let date;
            try {
                date = window.CampaignTime.today();   // the organisation's day (organisation-time-zone MP-07)
            } catch (e) {
                Modal.toast(`Not downloaded: ${e.message}`, 'error');
                return;
            }
            const filename = `${productName.replace(/[^a-zA-Z0-9]/g, '-')}-discovery-${date}.json`;

            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            Modal.toast('Discovery data downloaded', 'success');
        }

        /**
         * Upload and overwrite discovery data from a JSON file
         */
        async uploadDiscovery(event) {
            // Replacing every answer from a file writes straight to storage and the server, around
            // the guarded save. A read-only view must not offer it, and must not perform it if asked.
            if (this.viewOnly) return;
            const file = event.target.files?.[0];
            if (!file) return;

            // Reset file input so same file can be re-selected
            event.target.value = '';

            // Validate file size (500KB max)
            if (file.size > 512000) {
                Modal.alertModal('File Too Large', 'Discovery files must be under 500KB.');
                return;
            }

            try {
                const text = await file.text();
                let uploadedData;
                try {
                    uploadedData = JSON.parse(text);
                } catch (parseErr) {
                    Modal.alertModal('Invalid File', 'The selected file is not valid JSON.');
                    return;
                }

                // Validate it looks like discovery data
                if (typeof uploadedData !== 'object' || Array.isArray(uploadedData)) {
                    Modal.alertModal('Invalid Format', 'The file does not contain valid discovery data.');
                    return;
                }
                if (!uploadedData.productName && !uploadedData.brandName) {
                    Modal.alertModal('Missing Required Fields', 'The file must contain at least a product name or brand name.');
                    return;
                }

                // Strip export metadata if present
                delete uploadedData._exportMeta;

                // Show overwrite confirmation
                const confirmed = await Modal.confirmModal(
                    'Overwrite Discovery Data?',
                    'This will replace all current discovery answers with the uploaded file. This action cannot be undone.'
                );

                if (!confirmed) return;

                // Preserve campaignId — we're replacing answers, not creating a new campaign
                uploadedData.campaignId = this.campaignId;

                // Ensure brandName for server
                if (!uploadedData.brandName && uploadedData.productName) {
                    uploadedData.brandName = uploadedData.productName;
                }

                // Apply uploaded data to form immediately (works without auth)
                this.formData = { ...this.getEmptyForm(), ...uploadedData };
                // An uploaded file is another way in — a retired type is dropped here too.
                this.formData.promotionType = keepPromotionType(this.formData.promotionType);
                localStorage.setItem(this.draftKey, JSON.stringify(this.formData));

                // Try server save (non-blocking if auth unavailable)
                let serverSaved = false;
                try {
                    const apiBase = window.CAMPAIGNS_CONFIG.DISCOVERY_API_URL;

                    const repository = new DiscoveryRepository(apiBase);
                    await repository.saveCampaignDraft(uploadedData, this.nexusId || null);
                    serverSaved = true;

                    // Clear the multi-framework session so user must re-select frameworks
                    try {
                        const dashboardBase = window.CAMPAIGNS_CONFIG.DASHBOARD_API_URL;

                        const buildAuthHeaders = () => {
                            const nexusToken = sessionStorage.getItem('nexus_access_token');
                            if (nexusToken) {
                                return { Authorization: `Bearer ${nexusToken}` };
                            }
                            if (window.AuthService) {
                                return window.AuthService.getAuthHeaders();
                            }
                            return {};
                        };

                        await NexusConfig.fetchWithAuthRetry(() => fetch(`${dashboardBase}/api/framework/${this.campaignId}/session`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', ...buildAuthHeaders() },
                            body: JSON.stringify({
                                selectedFrameworks: [],
                                discoveryData: uploadedData,
                                activeFramework: ''
                            })
                        }));
                    } catch (sessionErr) {
                        console.warn('[Discovery] Could not clear framework session:', sessionErr.message);
                    }
                } catch (saveErr) {
                    console.warn('[Discovery] Server save failed (data saved locally):', saveErr.message);
                }

                // Re-render form with uploaded data
                this.currentSection = 0;
                this.render();
                Modal.toast(serverSaved ? 'Discovery data imported and saved' : 'Discovery data imported (saved locally)', 'success');

            } catch (error) {
                console.error('[Discovery] Upload error:', error);
                Modal.alertModal('Upload Failed', `Failed to import discovery data: ${error.message}`);
            }
        }

        /**
         * Show the Share Discovery Form dialog.
         * Generates a shareable link with optional password and expiration.
         */
        async showShareFormDialog(kind = 'discovery') {
            if (!this.campaignId) {
                Modal.alertModal('Cannot Share', 'Save the campaign first before sharing.');
                return;
            }
            const isTeaser = kind === 'teaser';   // MP-11: same popup + options; only the created link differs

            // Build custom modal content as DOM element (confirmModal wraps strings in <p> as text)
            const modalContent = document.createElement('div');
            modalContent.className = 'share-form-dialog';
            modalContent.innerHTML = `
                <div class="share-warning" style="background: var(--color-warning-50); border: 1px solid var(--color-warning-200); border-radius: 0.5rem; padding: 0.75rem; margin-bottom: 1rem;">
                    <span style="color: var(--color-warning-700);">${isTeaser ? '⚠️ Your prospect answers eight quick questions to get five ads — their answers build this campaign\'s discovery.' : '⚠️ When your prospect submits this form, their answers will replace any existing discovery data for this campaign.'}</span>
                </div>
                <div class="form-group" style="margin-bottom: 1rem;">
                    ${isTeaser ? `
                    <label style="display: block; margin-bottom: 0.25rem; font-weight: 500; color: var(--text-primary);">Prospect's name <span class="required-indicator" style="color: var(--color-danger, #dc2626);">*</span></label>
                    <input type="text" id="share-prospect-name" required placeholder="e.g., Jane Doe" style="width: 100%; padding: 0.5rem; border: 1px solid var(--border-primary); border-radius: 0.375rem; background: var(--bg-primary); color: var(--text-primary); box-sizing: border-box; margin-bottom: 0.75rem;">
                    <label style="display: block; margin-bottom: 0.25rem; font-weight: 500; color: var(--text-primary);">Prospect's email <span class="required-indicator" style="color: var(--color-danger, #dc2626);">*</span></label>
                    <input type="email" id="share-prospect-email" required placeholder="e.g., jane@example.com" style="width: 100%; padding: 0.5rem; border: 1px solid var(--border-primary); border-radius: 0.375rem; background: var(--bg-primary); color: var(--text-primary); box-sizing: border-box; margin-bottom: 0.75rem;">
                    <div id="share-prospect-error" style="display: none; color: var(--color-danger, #dc2626); font-size: 0.85rem; margin-bottom: 0.75rem;">The prospect's name and email are required — the link is made for this person.</div>
                    ` : ''}
                    <label style="display: block; margin-bottom: 0.25rem; font-weight: 500; color: var(--text-primary);">Label <span class="required-indicator" style="color: var(--color-danger, #dc2626);">*</span></label>
                    <input type="text" id="share-label" required placeholder="e.g., John Smith's intake form" style="width: 100%; padding: 0.5rem; border: 1px solid var(--border-primary); border-radius: 0.375rem; background: var(--bg-primary); color: var(--text-primary); box-sizing: border-box;">
                    <div id="share-label-error" style="display: none; color: var(--color-danger, #dc2626); font-size: 0.85rem; margin-top: 0.25rem;">Label is required.</div>
                </div>
                <div class="form-group" style="margin-bottom: 1rem;">
                    <label style="display: block; margin-bottom: 0.25rem; font-weight: 500; color: var(--text-primary);">Password <span style="color: var(--text-tertiary); font-weight: 400;">(optional)</span></label>
                    <input type="text" id="share-password" placeholder="Leave blank for no password" style="width: 100%; padding: 0.5rem; border: 1px solid var(--border-primary); border-radius: 0.375rem; background: var(--bg-primary); color: var(--text-primary); box-sizing: border-box;">
                </div>
                <div class="form-group" style="margin-bottom: 1rem;">
                    <label style="display: block; margin-bottom: 0.25rem; font-weight: 500; color: var(--text-primary);">Expires in</label>
                    <select id="share-expiry" style="width: 100%; padding: 0.5rem; border: 1px solid var(--border-primary); border-radius: 0.375rem; background: var(--bg-primary); color: var(--text-primary);">
                        <option value="0">Never</option>
                        <option value="1">1 day</option>
                        <option value="3">3 days</option>
                        <option value="7" selected>7 days</option>
                        <option value="30">30 days</option>
                    </select>
                </div>
            `;

            // Use Modal directly (not confirmModal) to avoid <p> wrapper mangling HTML content
            let formValues = null;   // captured inside the confirm handler, BEFORE close() destroys the modal DOM
            const confirmed = await new Promise((resolve) => {
                const confirmBtn = document.createElement('button');
                confirmBtn.className = 'btn btn-primary';
                confirmBtn.textContent = 'Generate Link';

                const cancelBtn = document.createElement('button');
                cancelBtn.className = 'btn btn-secondary';
                cancelBtn.textContent = 'Cancel';

                const modal = new Modal({
                    title: isTeaser ? 'Ad Builder' : 'Share Discovery Form',
                    content: modalContent,
                    footer: [confirmBtn, cancelBtn],
                    onClose: () => resolve(false)
                });

                confirmBtn.addEventListener('click', () => {
                    // Validate required label before closing the modal
                    const labelInput = document.getElementById('share-label');
                    const errorEl = document.getElementById('share-label-error');
                    const value = (labelInput?.value || '').trim();
                    if (!value) {
                        if (errorEl) errorEl.style.display = 'block';
                        if (labelInput) labelInput.focus();
                        return;
                    }
                    if (errorEl) errorEl.style.display = 'none';
                    // MP-05: a teaser link is made FOR a person — refuse the modal without both.
                    let prospectName = '', prospectEmail = '';
                    if (isTeaser) {
                        prospectName = (document.getElementById('share-prospect-name')?.value || '').trim();
                        prospectEmail = (document.getElementById('share-prospect-email')?.value || '').trim();
                        const pErr = document.getElementById('share-prospect-error');
                        // MP-05 review S6: the identity drives the rate limit and the CRM tag — a non-email
                        // must never become a person. Same shape check the server applies.
                        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(prospectEmail);
                        if (!prospectName || !prospectEmail || !emailOk) {
                            if (pErr) { pErr.textContent = !emailOk && prospectEmail ? 'That email address does not look right.' : 'The prospect\'s name and email are required — the link is made for this person.'; pErr.style.display = 'block'; }
                            document.getElementById(!prospectName ? 'share-prospect-name' : 'share-prospect-email')?.focus();
                            return;
                        }
                        if (pErr) pErr.style.display = 'none';
                    }
                    // Capture ALL inputs NOW — close() → destroy() removes the modal DOM, so a later
                    // getElementById would return null and label/password/expiry would be lost (→ 400).
                    formValues = {
                        label: value,
                        password: document.getElementById('share-password')?.value || '',
                        expiresInDays: parseInt(document.getElementById('share-expiry')?.value || '7', 10),
                        prospectName, prospectEmail
                    };
                    resolve(true);
                    modal.close();
                });
                cancelBtn.addEventListener('click', () => { resolve(false); modal.close(); });

                modal.mount(document.body);
            });

            if (!confirmed || !formValues) return;

            const { label, password, expiresInDays, prospectName, prospectEmail } = formValues;

            try {
                // Get API base for gateway calls. Teaser links are minted by the content-generator (MP-11);
                // discovery links by the discovery-form-links route. Same options either way — only the endpoint
                // and the resulting link differ.
                const gwBase = window.CAMPAIGNS_CONFIG.GATEWAY_URL;
                const underCampaigns = window.location.pathname.startsWith('/campaigns/');
                const generateUrl = isTeaser
                    ? gwBase + (underCampaigns ? '/campaigns/api/content-generator/teaser-link' : '/api/content-generator/teaser-link')
                    : gwBase + (underCampaigns ? '/campaigns/api/discovery-form-links/generate' : '/api/discovery-form-links/generate');

                const repository = new DiscoveryRepository('');

                const response = await repository._fetch(generateUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        campaignId: this.campaignId,
                        label,
                        password: password || undefined,
                        expiresInDays: expiresInDays || undefined,
                        // MP-05: the link is made for this person — built in at creation.
                        prospectName: isTeaser ? prospectName : undefined,
                        prospectEmail: isTeaser ? prospectEmail : undefined
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    // The server returns { error: '<reason string>' }; some paths return { error: { message } }.
                    // Surface the actual reason (a generic "Failed to generate link" hid the real cause before).
                    throw new Error(errorData.error?.message || errorData.error || 'Failed to generate link');
                }

                const result = await response.json();

                // Show the generated link
                const fullUrl = result.fullUrl || `${window.location.origin}${result.url}`;

                const linkContent = document.createElement('div');
                linkContent.style.textAlign = 'center';
                linkContent.innerHTML = `
                    <p style="color: var(--text-secondary); margin-bottom: 1rem;">${result.existing ? (result.personOnLink && result.personOnLink.email && prospectEmail && result.personOnLink.email !== prospectEmail ? `This campaign already has its one Ad Builder link, and it belongs to <b>${String(result.personOnLink.name || result.personOnLink.email).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]))}</b> — the details you just typed were NOT used. Here is that link:` : 'This campaign already has an Ad Builder link — here it is again:') : 'Share this link with your prospect:'}</p>
                    <div id="share-link-address" style="background: var(--bg-tertiary); border: 1px solid var(--border-primary); border-radius: 0.5rem; padding: 0.75rem; word-break: break-all; font-family: monospace; font-size: 0.9rem; color: var(--text-primary); margin-bottom: 1rem; user-select: all;">
                        ${fullUrl}
                    </div>
                    <button type="button" id="copy-link-btn" class="btn-primary" style="margin: 0 auto;">Copy Link</button>
                    ${result.expiresAt ? `<p style="color: var(--text-tertiary); font-size: 0.85rem; margin-top: 0.75rem;">Expires: ${window.CampaignTime.date(result.expiresAt, 'monthDayYear')}</p>` : ''}
                `;

                // Show link modal using Modal directly
                const closeBtn = document.createElement('button');
                closeBtn.className = 'btn btn-secondary';
                closeBtn.textContent = 'Close';

                const linkModal = new Modal({
                    title: result.existing ? 'Your Ad Builder Link' : 'Link Generated!',
                    content: linkContent,
                    footer: [closeBtn],
                    onClose: () => {}
                });

                closeBtn.addEventListener('click', () => linkModal.close());
                linkModal.mount(document.body);

                // Wire up copy button (now inside the mounted modal)
                const copyBtn = document.getElementById('copy-link-btn');
                if (copyBtn) {
                    // Never silent, never a false "Copied!": the async Clipboard API can be
                    // refused inside the NEXUS frame, and the legacy execCommand fallback can
                    // fail too. Success says Copied!; failure selects the address for a
                    // manual ⌘C / Ctrl+C and says so on the button.
                    const showCopied = () => {
                        copyBtn.textContent = 'Copied!';
                        setTimeout(() => { copyBtn.textContent = 'Copy Link'; }, 2000);
                    };
                    const showCopyFailed = () => {
                        const address = document.getElementById('share-link-address');
                        try {
                            const range = document.createRange();
                            range.selectNodeContents(address);
                            const selection = window.getSelection();
                            selection.removeAllRanges();
                            selection.addRange(range);
                        } catch { /* selection is best-effort */ }
                        // Touch devices get a long-press instruction: iPadOS Safari reports itself
                        // as a Mac, so a platform check alone would tell an iPad user to press ⌘C.
                        const touch = (navigator.maxTouchPoints || 0) > 1;
                        const key = (navigator.platform || '').indexOf('Mac') > -1 ? '⌘C' : 'Ctrl+C';
                        copyBtn.textContent = touch
                            ? 'Could not copy — address selected, long-press it to copy'
                            : `Could not copy — address selected, press ${key}`;
                        setTimeout(() => { copyBtn.textContent = 'Copy Link'; }, 8000);
                    };
                    copyBtn.addEventListener('click', async () => {
                        try {
                            await navigator.clipboard.writeText(fullUrl);
                            showCopied();
                        } catch {
                            let copied = false;
                            const textArea = document.createElement('textarea');
                            try {
                                textArea.value = fullUrl;
                                textArea.setAttribute('readonly', '');
                                textArea.style.cssText = 'position:absolute;left:-9999px;top:0';
                                document.body.appendChild(textArea);
                                textArea.select();
                                copied = document.execCommand('copy');
                            } catch { copied = false; }
                            finally { if (textArea.parentNode) textArea.parentNode.removeChild(textArea); }
                            if (copied) showCopied(); else showCopyFailed();
                        }
                    });
                }

            } catch (error) {
                console.error('[Discovery] Share form error:', error);
                Modal.alertModal('Share Failed', `Failed to generate share link: ${error.message}`);
            }
        }

        loadDraft() {
            const draft = localStorage.getItem(this.draftKey);
            return draft ? JSON.parse(draft) : null;
        }

        clearDraft() {
            localStorage.removeItem(this.draftKey);
        }

        startAutosave() {
            // One timer, ever: render() calls this on every render, and an uncleared interval
            // would stack a new 30-second saver each time (review: the leak predates this block).
            if (this.autosaveInterval) clearInterval(this.autosaveInterval);
            this.autosaveInterval = setInterval(() => {
                this.saveCurrentSection();
            }, 30000); // Every 30 seconds
        }

        /**
         * Say what actually happened. This used to flash "saved" the instant a key was pressed,
         * whether or not the answers ever reached the server — so a person whose save was refused
         * carried on believing their work was stored. A success clears itself after a moment; a
         * failure stays put, and survives a tab change, until a save actually succeeds.
         */
        showAutosaveIndicator(result) {
            const outcome = result || { ok: true };
            this.lastSaveFailure = outcome.ok ? null : outcome;

            const indicator = document.getElementById('autosave-indicator');
            if (!indicator) return;

            if (this.autosaveIndicatorTimer) {
                clearTimeout(this.autosaveIndicatorTimer);
                this.autosaveIndicatorTimer = null;
            }

            indicator.textContent = outcome.message || (this.editMode ? 'Changes saved' : 'Draft saved');
            if (outcome.ok) {
                indicator.classList.remove('autosave-indicator--failed');
            } else {
                indicator.classList.add('autosave-indicator--failed');
            }
            indicator.style.display = 'block';

            if (outcome.ok) {
                this.autosaveIndicatorTimer = setTimeout(() => {
                    indicator.style.display = 'none';
                }, 2000);
            }
        }

        getProgressPercent() {
            return (this.getCompletedQuestions() / this.getTotalQuestions()) * 100;
        }

        getCompletedQuestions() {
            const last = this.externalMode ? 5 : 6;
            let done = 0;
            for (let n = 0; n <= last; n++) done += this.countSection(n).done;
            return done;
        }

        /** ONE counting source (review C1/I2): done and total per section, path-aware. Both the
         *  tab strip and the header bar read THIS, so neither can disagree with the questions. */
        countSection(num) {
            const d = this.formData;
            const has = (v) => Array.isArray(v) ? v.some((x) => x && x.trim && x.trim()) : !!(v && (!v.trim || v.trim()));
            const items = [];
            switch (num) {
                case 0:
                    items.push(d.promotionType);
                    if (d.promotionType === 'event') items.push(d.eventName, d.eventDate, d.eventStartTime, d.eventEndTime, d.eventFormat, d.eventLocation, d.eventHost, d.eventHostCredential);
                    if (d.promotionType === 'news') items.push(d.announcementSubject, d.announcementWhen, d.announcementImpact);
                    // ads-brief-per-type-cutover step 5: each type's own answers count toward its own
                    // progress. The tab strip and the header bar read this, so leaving them out would
                    // show a course as complete while it still cannot produce an ad.
                    if (d.promotionType === 'course') items.push(d.courseStarts, d.courseTeacher);
                    if (d.promotionType === 'service') items.push(d.serviceArea, d.serviceAvailability);
                    break;
                case 1:
                    items.push(d.productName, d.productDescription, d.productCategory, d.pricePoint, d.purchaseType, d.offerHasDeal);
                    if (d.offerHasDeal === 'yes') items.push(d.offerNormalPrice, d.offerPrice, d.offerEnds);
                    break;
                case 2:
                    items.push(d.targetBuyer, d.awarenessLevel, d.buyerPsychology, d.purchaseTimeline, d.triggerEvents, d.failedApproaches);
                    break;
                case 3:
                    items.push(d.keyBenefits, d.uniqueValue, d.enemyName, d.prospectLanguage, d.socialProof, d.proofNumber, d.proofQuoteName, d.proofQuoteRole);
                    // OWNER RULING 2026-08-29: all five types are shown these three, so all five
                    // count them. None of them blocks completion.
                    items.push(d.painPoints, d.problemCost, d.problemCostPeriod);
                    // Only a product is asked the objection, so only a product counts it — a question
                    // that is never shown would otherwise hold the bar below full for ever.
                    if (d.promotionType === 'product') items.push(d.topObjection);
                    items.push(Array.isArray(d.proofAssets) && d.proofAssets.length ? 'x' : '');
                    break;
                case 4:
                    items.push(d.campaignGoal, d.offerDetails, d.callToAction, d.conversionDestination, d.actionUrl);
                    if (this.isNexusMode) items.push(d.contentType);
                    break;
                case 5:
                    items.push(d.brandTones);
                    items.push(Number.isInteger(d.adIntensity) ? 'set' : '');
                    break;
                case 6:
                    items.push(d.brandVoice);
                    break;
            }
            return { done: items.filter(has).length, total: items.length };
        }

        getTotalQuestions() {
            const last = this.externalMode ? 5 : 6;
            let total = 0;
            for (let n = 0; n <= last; n++) total += this.countSection(n).total;
            return total;
        }

        getSectionProgress(sectionNum) {
            const c = this.countSection(sectionNum);
            return `${c.done}/${c.total}`;
        }
    }
